import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Descriptions,
  List,
  Button,
  Skeleton,
  Select,
  Tag,
  notification,
} from "antd";
import dayjs from "dayjs";
import { formatDate } from "../utils/dateUtils";
import { useAuth } from "../hooks/useAuth";
import UpdateReviewModal from "./UpdateReviewModal";
import ReviewStatusTag from "./ReviewStatusTag";
import AttachmentsView from "./AttachmentsView.js";
import { getSubmittalById, updateSubmittal } from "../services/submittalsAPI";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { normalizeBooleanValue } from "../utils/projectUtils";
import { sanitizeHtml } from "../utils/textUtils";

const ViewSubmittalModal = ({
  visible,
  onClose,
  submittalId,
  isLocked = false,
  scrollToRespond = false,
  projectInformation = null,
}) => {
  const { currentUser, selectedProject } = useAuth();
  const queryClient = useQueryClient();
  const effectiveProject =
    projectInformation != null ? projectInformation : selectedProject;
  const userRole = effectiveProject?.role;
  const isOwnerOrManager =
    userRole === "owner" || userRole === "project_manager";
  const [isUpdateReviewModalVisible, setIsUpdateReviewModalVisible] =
    useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const reviewSectionRef = useRef(null);
  const trackMaterialRelease = normalizeBooleanValue(
    effectiveProject?.track_material_release,
  );

  const { data: submittal, refetch } = useQuery({
    queryKey: ["submittal", submittalId],
    queryFn: () => getSubmittalById(submittalId),
    enabled: !!submittalId,
    select: (data) => ({
      ...data,
      formatted_number: data.specification_section_details?.section_number
        ? `${data.specification_section_details.section_number}-${data.number}.${data.revision}`
        : `${data.number}.${data.revision}`,
      formatted_ball_in_courts: data?.ball_in_court_details
        .map((element) => element.username)
        .join(", "),
      formatted_received_from: data.received_from_details?.username,
      formatted_spec_section: data.specification_section_details
        ? data.specification_section_details?.title
        : "N/A",
    }),
    onError: (error) => {
      console.log("Failed to fetch submittal", error);
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }) => updateSubmittal(id, { status }, false),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["submittal", submittalId] });
      queryClient.invalidateQueries({ queryKey: ["submittals"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-open-submittals"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      notification.success({ message: "Status updated successfully" });
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update status",
        description: error.response?.data?.detail || error.message,
      });
    },
  });

  const handleSubmittalStatusChange = (newStatus) => {
    if (!submittal?.id) return;
    updateStatusMutation.mutate({ id: submittal.id, status: newStatus });
  };

  useEffect(() => {
    if (visible) {
      refetch();
    }
  }, [visible, refetch]);

  useEffect(() => {
    if (visible && scrollToRespond && submittal && reviewSectionRef.current) {
      const timer = setTimeout(() => {
        reviewSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [visible, scrollToRespond, submittal]);

  const handleUpdateReviewClick = (review) => {
    setSelectedReview(review);
    setIsUpdateReviewModalVisible(true);
  };

  const isAuthorized = (review) => {
    return (
      currentUser?.id === submittal.submittal_manager_id ||
      (review.reviewer_id === currentUser?.id && submittal.ball_in_court_ids
        ? submittal.ball_in_court_ids.some(
            (element) => element === currentUser?.id,
          )
        : false) ||
      effectiveProject?.role === "project_manager" ||
      effectiveProject?.role === "owner"
    );
  };

  return (
    <Modal
      title={
        submittal
          ? `Submittal ${submittal.formatted_number}${
              submittal.title ? ` - ${submittal.title}` : ""
            }`
          : "Submittal"
      }
      open={visible}
      onCancel={isLocked ? undefined : onClose}
      footer={null}
      width={800}
      closable={!isLocked}
      maskClosable={!isLocked}
      keyboard={!isLocked}
      styles={
        isLocked
          ? { mask: { backgroundColor: "rgba(179, 179, 179, 0.83)" } }
          : undefined
      }
    >
      {" "}
      {!submittal ? (
        <Skeleton active />
      ) : (
        <>
          <Descriptions
            bordered
            column={2}
            size="small"
            labelStyle={{ width: "9rem" }}
          >
            <Descriptions.Item label="Title" span={2}>
              {submittal.title}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              <div
                className="max-h-[240px] overflow-y-auto [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:list-inside [&_a]:text-blue-600 [&_a]:underline"
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(submittal.description || ""),
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Specification Section" span={2}>
              {submittal.specification_section_details
                ? `${submittal.specification_section_details.section_number} ${submittal.specification_section_details.title}`
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Number">
              {submittal.number}
            </Descriptions.Item>
            <Descriptions.Item label="Revision">
              {submittal.revision}
            </Descriptions.Item>
            <Descriptions.Item label="Issue Date">
              {submittal.issue_date ? formatDate(submittal.issue_date) : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Due Date">
              {submittal.due_date ? formatDate(submittal.due_date) : "N/A"}
            </Descriptions.Item>
            {trackMaterialRelease && (
              <Descriptions.Item label="Released On">
                {submittal.released_on
                  ? formatDate(submittal.released_on)
                  : "N/A"}
              </Descriptions.Item>
            )}
            {trackMaterialRelease && (
              <Descriptions.Item label="Estimated Delivery Date">
                {submittal.estimated_delivery_date
                  ? formatDate(submittal.estimated_delivery_date)
                  : "N/A"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Status">
              {isOwnerOrManager ? (
                <Select
                  value={submittal.status}
                  onChange={handleSubmittalStatusChange}
                  className="status-select-tag w-[120px]"
                  loading={updateStatusMutation.isPending}
                  disabled={updateStatusMutation.isPending}
                  popupRender={(menu) => menu}
                  suffixIcon={<span className="text-xs text-gray-400 ml-1">▼</span>}
                >
                  <Select.Option value="Open">
                    <Tag color="orange" className="m-0 w-full text-center block">
                      Open
                    </Tag>
                  </Select.Option>
                  <Select.Option value="Closed">
                    <Tag color="default" className="m-0 w-full text-center block">
                      Closed
                    </Tag>
                  </Select.Option>
                  <Select.Option value="Draft">
                    <Tag color="blue" className="m-0 w-full text-center block">
                      Draft
                    </Tag>
                  </Select.Option>
                </Select>
              ) : (
                <Tag
                  color={
                    {
                      Open: "orange",
                      Closed: "default",
                      Draft: "blue",
                    }[submittal.status] || "default"
                  }
                  className="m-0"
                >
                  {submittal.status}
                </Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Priority">
              {submittal.priority || "Medium"}
            </Descriptions.Item>
            {effectiveProject?.use_custom_terminology && (
              <Descriptions.Item label="EDMS" span={2}>
                {submittal.edms || "-"}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Submitted By">
              {submittal.submitted_by_details.username}
            </Descriptions.Item>
            <Descriptions.Item label="Received From">
              {submittal.received_from_details?.username || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Submitted For">
              {submittal.submitted_for || "For Approval"}
            </Descriptions.Item>
            <Descriptions.Item label="Submittal Manager">
              {submittal.submittal_manager_details.username}
            </Descriptions.Item>
            <Descriptions.Item label="Ball in Court" span={2}>
              {submittal.ball_in_court_details
                .map((person) => person.username)
                .join(", ")}
            </Descriptions.Item>
            <Descriptions.Item label="Cc" span={2}>
              {submittal.distribution_list_details
                .map((person) => person.username)
                .join(", ")}
            </Descriptions.Item>
            <Descriptions.Item label="Attachments" span={2}>
              <AttachmentsView attachments={submittal.attachments} />
            </Descriptions.Item>
            <Descriptions.Item label="Reviewers" span={2}>
              <div>
                <List
                  dataSource={submittal.steps}
                  renderItem={(step) => (
                    <List.Item>
                      <List.Item.Meta
                        title={`Step ${step.step_number}`}
                        description={
                          <List
                            dataSource={step.reviews}
                            renderItem={(review) => (
                              <List.Item
                                actions={[
                                  isAuthorized(review) && (
                                    <Button
                                      type="primary"
                                      onClick={() =>
                                        handleUpdateReviewClick({
                                          ...review,
                                          submittal_title: submittal.title,
                                          submittal_number: submittal.number,
                                          submittal_status: submittal.status,
                                          submittal_ball_in_court:
                                            submittal.ball_in_court_details,
                                        })
                                      }
                                    >
                                      Update Review
                                    </Button>
                                  ),
                                ]}
                              >
                                <List.Item.Meta
                                  title={review.reviewer_details.username}
                                  description={
                                    <>
                                      <div>
                                        Status:{" "}
                                        <ReviewStatusTag
                                          status={review.status}
                                          emptyLabel="N/A"
                                        />
                                      </div>
                                      <div>
                                        <div className="text-gray-600">
                                          Comment:
                                        </div>
                                        {review.comment ? (
                                          <div
                                            className="mt-1 max-w-full [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                                            dangerouslySetInnerHTML={{
                                              __html: sanitizeHtml(
                                                review.comment,
                                              ),
                                            }}
                                          />
                                        ) : (
                                          <span>N/A</span>
                                        )}
                                      </div>
                                      <div>
                                        Reviewed On:{" "}
                                        {review.reviewed_on
                                          ? dayjs(review.reviewed_on).format(
                                              "YYYY-MM-DD",
                                            )
                                          : "N/A"}
                                      </div>
                                      <div>
                                        <AttachmentsView
                                          attachments={review.attachments}
                                        />
                                      </div>
                                    </>
                                  }
                                />
                              </List.Item>
                            )}
                          />
                        }
                      />
                    </List.Item>
                  )}
                />
              </div>
            </Descriptions.Item>
          </Descriptions>
          <div ref={reviewSectionRef} />
          {selectedReview && (
            <UpdateReviewModal
              visible={isUpdateReviewModalVisible}
              onClose={() => {
                setIsUpdateReviewModalVisible(false);
              }}
              review={selectedReview}
              onCloseSubmittalModal={onClose}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default ViewSubmittalModal;
